/**
 * Error text for the CBP builder, shown with names instead of backend ids.
 *
 * Column ids (`<sourceId>:<column>` for inputs, `calc_<ts>_<n>` for calculated columns) and the pool id
 * are internal. Both the builder's own messages and the backend's (validation findings, cycle errors,
 * "Common Balance Pool <poolId> …") can carry them, so every error Alert passes its text through
 * {@link withNames} at render time.
 */
import { CommonBalancePoolColumn } from 'src/models/multidimensional-allocation/CommonBalancePool';

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Replace every known column id in `message` with the column's quoted name, and the pool id with the
 * pool's quoted name. An id already wrapped in quotes or brackets (`"id"`, `[id]`) is replaced together
 * with its wrapper, so the result never reads `""name""`. Ids with no name are left as-is.
 */
export function withNames(
    message: string | undefined,
    columns: Array<Pick<CommonBalancePoolColumn, 'columnId' | 'displayName'>> = [],
    pool?: { poolId?: string; poolName?: string },
): string | undefined {
    if (!message) return message;
    const names: Record<string, string> = {};
    columns.forEach((c) => {
        const name = (c.displayName || '').trim();
        if (c.columnId && name) names[c.columnId] = name;
    });
    if (pool?.poolId && pool.poolName?.trim()) names[pool.poolId] = pool.poolName.trim();
    // Longest first, so an id that contains another (e.g. a table id inside `<tableId>:<column>`) wins.
    const ids = Object.keys(names).sort((a, b) => b.length - a.length);
    if (ids.length === 0) return message;
    const alt = ids.map(escapeRegExp).join('|');
    // A wrapper is consumed only as a matched pair, so a lone quote next to an id is kept. A bare id must
    // stand alone, not inside a longer token: id `c1` is not replaced inside `c10` or `x:c1:y`. A colon
    // only joins tokens when a word character is on its far side, so `step calc_1: MAX(...)` (the
    // calculation engine's error format) still has its id replaced.
    // No lookbehind: it can't be transpiled and throws on browsers we still target (Safari < 16.4), so the
    // character(s) before a bare id are captured as `boundary` and written back: start of text, a character
    // other than a word character, `-` or `:`, or a `:` that does not follow a word character.
    const pattern = new RegExp(`"(${alt})"|'(${alt})'|\\[(${alt})\\]|(^|[^\\w:-]|(?:^|\\W):)(${alt})(?![\\w-]|:\\w)`, 'g');
    return message.replace(pattern, (_match, quoted?: string, singleQuoted?: string, bracketed?: string, boundary?: string, bare?: string) => {
        const id = (quoted ?? singleQuoted ?? bracketed ?? bare) as string;
        return `${boundary ?? ''}"${names[id]}"`;
    });
}

/**
 * The columns the builder's error Alerts resolve names from: the selected past period's version columns
 * (so a column only that version has still gets its name) and the current definition's. Current columns
 * come last because {@link withNames} keeps the last name per id, and the builder's errors are about the
 * current definition, so a column renamed since that period shows its current name.
 */
export function builderErrorColumns<T>(currentColumns?: T[] | null, pastColumns?: T[] | null): T[] {
    return [...(pastColumns ?? []), ...(currentColumns ?? [])];
}
